class FrestNewArrivals extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  async #load() {
    const grid = this.querySelector('[data-new-arrivals-grid]');
    const limit = Number(this.dataset.limit) || 20;
    if (!grid) return;

    try {
      const products = await this.#fetchProducts(limit);
      if (products.length === 0) return;

      grid.replaceChildren(...products.slice(0, limit).map((product) => this.#createCard(product)));
      this.dataset.ready = 'true';
    } catch (error) {
      console.debug('New arrivals fallback remains visible', error);
    }
  }

  async #fetchProducts(limit) {
    const products = [];
    const seen = new Set();
    const pageSize = 40;

    for (let page = 1; page <= 3 && products.length < limit; page += 1) {
      const url = new URL('/products.json', window.location.origin);
      url.searchParams.set('limit', String(pageSize));
      url.searchParams.set('page', String(page));

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Products request failed with ${response.status}`);

      const payload = await response.json();
      const pageProducts = Array.isArray(payload.products) ? payload.products : [];

      for (const product of pageProducts) {
        const tags = Array.isArray(product.tags) ? product.tags : [];
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const isAvailable = variants.some((variant) => variant.available);
        if (!product.id || seen.has(product.id) || tags.includes('COL-CALENDAR') || !isAvailable) continue;

        seen.add(product.id);
        products.push(product);
      }

      if (pageProducts.length < pageSize) break;
    }

    return products.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
  }

  #createCard(product) {
    const productUrl = `/products/${product.handle}`;
    const article = document.createElement('article');
    article.className = 'frest-home__product-card';

    const imageLink = document.createElement('a');
    imageLink.className = 'frest-home__product-image';
    imageLink.href = productUrl;

    const imageData = product.images?.[0];
    if (imageData?.src) {
      const image = document.createElement('img');
      image.src = this.#imageUrl(imageData.src, 900);
      image.srcset = [240, 320, 480, 640, 900, 1200]
        .map((width) => `${this.#imageUrl(imageData.src, width)} ${width}w`)
        .join(', ');
      image.sizes = '(min-width: 1280px) 220px, (min-width: 990px) calc((100vw - 8rem) / 5), (min-width: 750px) calc((100vw - 6rem) / 3), 50vw';
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = imageData.width || 900;
      image.height = imageData.height || 900;
      imageLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'frest-home__product-details';

    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.title = product.title;
    titleLink.textContent = this.#truncateTitle(product.title, 70);
    heading.append(titleLink);

    const meta = document.createElement('div');
    meta.className = 'frest-home__product-meta';

    const price = document.createElement('span');
    price.className = 'frest-home__price';
    const availableVariants = product.variants.filter((variant) => variant.available);
    const lowestVariant = availableVariants.reduce((lowest, variant) => {
      if (!lowest) return variant;
      return Number(variant.price) < Number(lowest.price) ? variant : lowest;
    }, null);

    const compareAtPrice = Number(lowestVariant?.compare_at_price || 0);
    const currentPrice = Number(lowestVariant?.price || 0);
    if (compareAtPrice > currentPrice) {
      const compare = document.createElement('s');
      compare.textContent = this.#formatMoney(compareAtPrice);
      price.append(compare);
    }
    price.append(document.createTextNode(this.#formatMoney(currentPrice)));

    const viewLink = document.createElement('a');
    viewLink.className = 'frest-home__view-link';
    viewLink.href = productUrl;
    viewLink.setAttribute('aria-label', `View ${product.title}`);
    viewLink.append(document.createTextNode('View '));
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    viewLink.append(arrow);

    meta.append(price, viewLink);
    details.append(heading, meta);
    article.append(imageLink, details);
    return article;
  }

  #imageUrl(source, width) {
    const url = new URL(source);
    url.searchParams.set('width', String(width));
    return url.toString();
  }

  #formatMoney(value) {
    const locale = this.dataset.locale || 'en-US';
    const currency = this.dataset.currency || 'USD';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
  }

  #truncateTitle(title, maximumLength) {
    if (title.length <= maximumLength) return title;

    let result = '';
    for (const word of title.split(/\s+/)) {
      const candidate = `${result} ${word}`.trim();
      if (candidate.length > maximumLength && result) break;
      result = candidate;
    }
    return `${result}…`;
  }
}

if (!customElements.get('frest-new-arrivals')) {
  customElements.define('frest-new-arrivals', FrestNewArrivals);
}
